'use client';

import { useState, useEffect, useRef } from 'react';
import { Shield, AlertTriangle, MessageSquare, Loader2, ChevronDown, Check, ChevronRight, Lock } from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useToast } from '@/hooks/useToast';
import { useAgent } from '@/hooks/useAgent';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface AbuseAndEthicsSettingsProps {
  agentId: string;
  agentName: string;
  agentType?: 'WIDGET' | 'CHAT_APP';
}

type ActionType = 'no_action' | 'log' | 'disable';
type SensitivityLevel = 'high' | 'medium' | 'low';

export default function AbuseAndEthicsSettings({ agentId, agentName, agentType }: AbuseAndEthicsSettingsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { agent, updateAgent } = useAgent(agentId);
  const { chatSafetyEnabled, chatAiConsentEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);
  
  // Use agent type from prop or from agent data
  const effectiveAgentType = agentType || agent?.agentType;
  const [isSavingThreat, setIsSavingThreat] = useState(false);
  const [isSavingAbuse, setIsSavingAbuse] = useState(false);
  const [penaltyDropdownOpen, setPenaltyDropdownOpen] = useState<'threat' | 'abuse' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [settings, setSettings] = useState({
    // AI Consent
    requireAIConsent: false,
    
    // Threat Detection
    detectThreat: false,
    threatAction: 'log' as ActionType,
    threatWarningsBeforePenalty: 3,
    threatWarningsBeforeDisable: 5,
    threatPenaltyDuration: 60, // minutes

    // Abuse Detection
    detectAbuse: false,
    abuseSensitivity: 'medium' as SensitivityLevel,
    abuseAction: 'log' as ActionType,
    abuseWarningsBeforePenalty: 3,
    abuseWarningsBeforeDisable: 5,
    abusePenaltyDuration: 60, // minutes
  });

  // Load settings from agent data
  useEffect(() => {
    if (agent?.configDoc?.abuseAndEthics) {
      setSettings((prev) => ({
        ...prev,
        ...agent.configDoc.abuseAndEthics,
      }));
    }
  }, [agent]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setPenaltyDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAutoSave = async (newSettings: typeof settings) => {
    try {
      await updateAgent.mutateAsync({ abuseAndEthics: newSettings });
      showToast(t('admin.appearance.autoSaved'), 'success');
      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (error) {
      console.error('Failed to save abuse and ethics settings:', error);
      showToast(t('admin.abuseAndEthics.errors.saveFailed'), 'error');
      throw error; // Re-throw to revert changes
    }
  };

  const handleToggleThreat = async () => {
    if (isSavingThreat) return;

    const newValue = !settings.detectThreat;
    const newSettings = { ...settings, detectThreat: newValue };
    setSettings(newSettings);
    setIsSavingThreat(true);

    try {
      await handleAutoSave(newSettings);
    } catch (error) {
      // Revert on error
      setSettings((prev) => ({ ...prev, detectThreat: !newValue }));
    } finally {
      setIsSavingThreat(false);
    }
  };

  const handleToggleAbuse = async () => {
    if (isSavingAbuse) return;

    const newValue = !settings.detectAbuse;
    const newSettings = { ...settings, detectAbuse: newValue };
    setSettings(newSettings);
    setIsSavingAbuse(true);

    try {
      await handleAutoSave(newSettings);
    } catch (error) {
      // Revert on error
      setSettings((prev) => ({ ...prev, detectAbuse: !newValue }));
    } finally {
      setIsSavingAbuse(false);
    }
  };

  const handleSettingChange = async (key: keyof typeof settings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    try {
      await handleAutoSave(newSettings);
    } catch (error) {
      // Revert on error
      setSettings((prev) => ({ ...prev, [key]: settings[key as keyof typeof settings] }));
    }
  };

  const penaltyOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  if (!agent || loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8">
            {/* Threat Protection skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="space-y-4">
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-32 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            </div>

            {/* Abuse Monitoring skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="space-y-4">
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-32 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.title')}</h1>
            {!chatSafetyEnabled && (
              <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                {t('admin.sidebar.badges.enterprise')}
              </span>
            )}
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{t('admin.abuseAndEthics.description')}</p>
        </div>

        <div className="space-y-8">
          {/* AI Consent Section */}
          {(effectiveAgentType === 'WIDGET' || effectiveAgentType === 'CHAT_APP') && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.aiConsent.title')}</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t('admin.abuseAndEthics.aiConsent.description')}
            </p>
            
            {!chatAiConsentEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Shield className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('admin.abuseAndEthics.aiConsent.locked.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('admin.abuseAndEthics.aiConsent.locked.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://platform.vectorize.io'}/dashboard/pricing`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        Upgrade to Pro
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('admin.abuseAndEthics.aiConsent.locked.upgradeMessage')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className={`flex items-center justify-between ${!chatAiConsentEnabled ? 'opacity-50' : ''}`}>
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('admin.abuseAndEthics.aiConsent.enable')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {effectiveAgentType === 'WIDGET' 
                    ? t('admin.abuseAndEthics.aiConsent.enableDescription')
                    : t('admin.abuseAndEthics.aiConsent.enableDescriptionApp')}
                </p>
              </div>
              <button
                onClick={async () => {
                  if (!chatAiConsentEnabled) return;
                  const newValue = !settings.requireAIConsent;
                  const newSettings = { ...settings, requireAIConsent: newValue };
                  setSettings(newSettings);
                  try {
                    await handleAutoSave(newSettings);
                  } catch (error) {
                    setSettings((prev) => ({ ...prev, requireAIConsent: !newValue }));
                  }
                }}
                disabled={!chatAiConsentEnabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.requireAIConsent ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                } ${!chatAiConsentEnabled ? 'cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.requireAIConsent ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          )}

          {/* Threat Detection Section */}
          <div
            className={`bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 transition-opacity relative ${
              isSavingThreat ? 'opacity-60' : ''
            }`}
          >
            {!chatSafetyEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}
            <div className="flex items-center gap-2 mb-4 relative z-10">
              <AlertTriangle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.threatDetection.title')}</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 relative z-10">{t('admin.abuseAndEthics.threatDetection.description')}</p>

            {!chatSafetyEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('admin.abuseAndEthics.locked.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('admin.abuseAndEthics.locked.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://calendly.com/d/cnbp-y7b-m77"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        Talk to Sales
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('admin.abuseAndEthics.locked.upgradeMessage')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6 relative z-10">
              {/* Enable Threat Detection */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.threatDetection.enable')}</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.abuseAndEthics.threatDetection.enableDescription')}</p>
                </div>
                <button
                  onClick={handleToggleThreat}
                  disabled={isSavingThreat || !chatSafetyEnabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.detectThreat ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } ${isSavingThreat || !chatSafetyEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.detectThreat ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Action Settings */}
              {settings.detectThreat && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.actions.title')}</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">{t('admin.abuseAndEthics.actions.description')}</p>
                    <div className="space-y-2">
                      {(['no_action', 'log', 'disable'] as const).map((action) => (
                        <label key={action} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="threatAction"
                            value={action}
                            checked={settings.threatAction === action}
                            onChange={(e) => handleSettingChange('threatAction', e.target.value as ActionType)}
                            disabled={!chatSafetyEnabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {action === 'disable' && effectiveAgentType === 'WIDGET' 
                                ? t('admin.abuseAndEthics.actions.blockIp.label')
                                : t(`admin.abuseAndEthics.actions.${action}.label`)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {action === 'disable' && effectiveAgentType === 'WIDGET'
                                ? t('admin.abuseAndEthics.actions.blockIp.description')
                                : t(`admin.abuseAndEthics.actions.${action}.description`)}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Threshold Settings */}
                  {settings.threatAction === 'disable' && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {effectiveAgentType === 'WIDGET' 
                            ? t('admin.abuseAndEthics.thresholds.warningsBeforeBlock')
                            : t('admin.abuseAndEthics.thresholds.warningsBeforeDisable')}
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                          {effectiveAgentType === 'WIDGET'
                            ? t('admin.abuseAndEthics.thresholds.perDayWindowIp')
                            : t('admin.abuseAndEthics.thresholds.perDayWindow')}
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={settings.threatWarningsBeforeDisable}
                            onChange={(e) => handleSettingChange('threatWarningsBeforeDisable', parseInt(e.target.value) || 5)}
                            disabled={!chatSafetyEnabled}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {settings.threatWarningsBeforeDisable === 1
                              ? (effectiveAgentType === 'WIDGET' 
                                  ? t('admin.abuseAndEthics.thresholds.immediateBlock')
                                  : t('admin.abuseAndEthics.thresholds.immediateDisable'))
                              : (effectiveAgentType === 'WIDGET'
                                  ? t('admin.abuseAndEthics.thresholds.afterViolationsBlock', { count: settings.threatWarningsBeforeDisable })
                                  : t('admin.abuseAndEthics.thresholds.afterViolations', { count: settings.threatWarningsBeforeDisable }))}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Abuse Detection Section */}
          <div
            className={`bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 transition-opacity relative ${
              isSavingAbuse ? 'opacity-60' : ''
            }`}
          >
            {!chatSafetyEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}
            <div className="flex items-center gap-2 mb-4 relative z-10">
              <MessageSquare className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.abuseDetection.title')}</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 relative z-10">{t('admin.abuseAndEthics.abuseDetection.description')}</p>

            {!chatSafetyEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('admin.abuseAndEthics.locked.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('admin.abuseAndEthics.locked.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://calendly.com/d/cnbp-y7b-m77"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        Talk to Sales
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('admin.abuseAndEthics.locked.upgradeMessage')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6 relative z-10">
              {/* Enable Abuse Detection */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.abuseDetection.enable')}</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.abuseAndEthics.abuseDetection.enableDescription')}</p>
                </div>
                <button
                  onClick={handleToggleAbuse}
                  disabled={isSavingAbuse || !chatSafetyEnabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.detectAbuse ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } ${isSavingAbuse || !chatSafetyEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.detectAbuse ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Abuse Settings */}
              {settings.detectAbuse && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Sensitivity Level */}
                  <div>
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('admin.abuseAndEthics.abuseDetection.sensitivity')}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
                      {t('admin.abuseAndEthics.abuseDetection.sensitivityDescription')}
                    </p>
                    <div className="space-y-2">
                      {(['high', 'medium', 'low'] as const).map((level) => (
                        <label key={level} className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="abuseSensitivity"
                            value={level}
                            checked={settings.abuseSensitivity === level}
                            onChange={(e) => handleSettingChange('abuseSensitivity', e.target.value as SensitivityLevel)}
                            disabled={!chatSafetyEnabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 mt-0.5"
                          />
                          <div>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {t(`admin.abuseAndEthics.abuseDetection.levels.${level}.label`)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t(`admin.abuseAndEthics.abuseDetection.levels.${level}.description`)}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Action Settings */}
                  <div>
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('admin.abuseAndEthics.actions.title')}</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">{t('admin.abuseAndEthics.actions.description')}</p>
                    <div className="space-y-2">
                      {(['no_action', 'log', 'disable'] as const).map((action) => (
                        <label key={action} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="abuseAction"
                            value={action}
                            checked={settings.abuseAction === action}
                            onChange={(e) => handleSettingChange('abuseAction', e.target.value as ActionType)}
                            disabled={!chatSafetyEnabled}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {action === 'disable' && effectiveAgentType === 'WIDGET' 
                                ? t('admin.abuseAndEthics.actions.blockIp.label')
                                : t(`admin.abuseAndEthics.actions.${action}.label`)}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {action === 'disable' && effectiveAgentType === 'WIDGET'
                                ? t('admin.abuseAndEthics.actions.blockIp.description')
                                : t(`admin.abuseAndEthics.actions.${action}.description`)}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Threshold Settings */}
                  {settings.abuseAction === 'disable' && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {effectiveAgentType === 'WIDGET' 
                            ? t('admin.abuseAndEthics.thresholds.warningsBeforeBlock')
                            : t('admin.abuseAndEthics.thresholds.warningsBeforeDisable')}
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                          {effectiveAgentType === 'WIDGET'
                            ? t('admin.abuseAndEthics.thresholds.perDayWindowIp')
                            : t('admin.abuseAndEthics.thresholds.perDayWindow')}
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={settings.abuseWarningsBeforeDisable}
                            onChange={(e) => handleSettingChange('abuseWarningsBeforeDisable', parseInt(e.target.value) || 5)}
                            disabled={!chatSafetyEnabled}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {settings.abuseWarningsBeforeDisable === 1
                              ? (effectiveAgentType === 'WIDGET' 
                                  ? t('admin.abuseAndEthics.thresholds.immediateBlock')
                                  : t('admin.abuseAndEthics.thresholds.immediateDisable'))
                              : (effectiveAgentType === 'WIDGET'
                                  ? t('admin.abuseAndEthics.thresholds.afterViolationsBlock', { count: settings.abuseWarningsBeforeDisable })
                                  : t('admin.abuseAndEthics.thresholds.afterViolations', { count: settings.abuseWarningsBeforeDisable }))}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
